import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ChannelSelectorModalComponent } from 'src/app/components/channel-selector/channel-selector-modal/channel-selector-modal.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { SupportStepsModule } from 'src/app/components/support-steps/support-steps.module';
import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { INBOX_TYPES, routes, WorkingHours } from 'src/app/constants';
import { fileNameFromImageModalUpload } from 'src/app/utils/utils';
import { SlackChannel, TicketType } from 'src/models';
import { ProjectEscalationSettings, ProjectVisibility } from 'src/models/projects';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { UploadService } from 'src/services/upload.service';

import { CustomFieldsModule } from '../../components/custom-fields/custom-fields.module';

@Component({
  standalone: true,
  selector: 'app-edit-project',
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    SupportStepsModule,
  ],
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
})
export class EditProjectComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;

  isLoading = false;
  isInitialized = false;
  name: string;
  originalName: string;
  description: string;
  id?: string;
  visibility: ProjectVisibility;
  isExternal: boolean;
  defaultTriageChannelId: string | null = null;
  defaultTriageChannel: SlackChannel | null = null;
  supportName: string | null = null;
  supportLogo: string | null = null;
  workingHours: WorkingHours | null = null;
  workingHoursEnabled: boolean = false;
  supportSteps: any[] = [];
  defaultTicketType: TicketType | null = null;
  instructions: string | null = null;
  triageMessageTemplateId: string | null = null;
  triageMessageTemplate: any | null = null;
  escalationSettings: ProjectEscalationSettings = {
    sendUserFacingNotifications: false,
  };
  showEscalationSettings = false;
  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private projectsService: ProjectsService,
    private slackService: SlackService,
    private uploadService: UploadService,
    private navCtrl: NavController,
    private ticketTypesService: TicketTypesService,
    private searchablePopoverService: SearchablePopoverService,
    private apiService: ApiService,
  ) {
    this.id = this.route.snapshot.params['projectId'];
  }

  ngOnInit(): void {
    if (this.id) {
      this.loadProject();
    } else {
      this.visibility = 'public';
      this.isExternal = false;
      this.isInitialized = true;

      setTimeout(() => this.nameInput.setFocus(), 500);
    }
  }

  async loadProject() {
    try {
      const project = await this.projectsService.getById(this.id);
      const tenant = this.authService.tenant;
      this.name = project.name;
      this.originalName = project.name;
      this.description = project.description;
      this.visibility = project.visibility;
      this.isExternal = project.isExternal;
      this.defaultTriageChannelId = project.defaultTriageChannelId;
      this.triageMessageTemplateId = project.triageMessageTemplateId;
      this.supportName = project.supportName;
      this.supportLogo = project.supportLogo;
      this.workingHours = project.workingHours;
      this.workingHoursEnabled = project.workingHours && project.workingHours.enabled;
      this.supportSteps = project.supportSteps;
      this.instructions = project.instructions;
      this.escalationSettings = project.escalationSettings;
      this.showEscalationSettings = tenant.featureFlags?.escalationsEnabled;
      if (project?.defaultTicketTypeId) {
        this.loadDefaultTicketType(project.defaultTicketTypeId);
      }

      if (project?.defaultTriageChannelId) {
        this.loadTriageChannel();
      }

      if (project?.triageMessageTemplateId) {
        this.loadTriageMessageTemplate();
      }
      this.isInitialized = true;
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  async loadDefaultTicketType(id: string) {
    const ticketTypesResponse = await this.ticketTypesService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'id',
          operator: '==',
          value: id,
        },
      ],
    });

    this.defaultTicketType = ticketTypesResponse.data?.[0] as TicketType;
  }

  async loadTriageChannel() {
    try {
      const triageChannel = await this.slackService.getChannel(this.defaultTriageChannelId);

      if (triageChannel) {
        this.defaultTriageChannel = triageChannel;
      }
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  async loadTriageMessageTemplate() {
    try {
      const template = await this.apiService.getPromise(`/templates/${this.triageMessageTemplateId}`);

      this.triageMessageTemplate = template;
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  async setTriageMessageTemplate() {
    this.searchablePopoverService.templates({
      event,
      showNoneOption: true,
      selectedTemplateId: this.triageMessageTemplateId,
      callback: (id: string, _title: string, data: any) => {
        if (this.triageMessageTemplateId === id) {
          this.triageMessageTemplateId = null;
          this.triageMessageTemplate = null;
        } else {
          this.triageMessageTemplateId = id;
          this.triageMessageTemplate = data;
        }
      },
    });
  }

  channelDidChange(slackChannel: SlackChannel) {
    this.defaultTriageChannel = slackChannel;
    this.defaultTriageChannelId = slackChannel?.id ?? null;
  }

  async setDefaultTriageChannel() {
    const modal = await this.modalCtrl.create({
      component: ChannelSelectorModalComponent,
      componentProps: {
        selectedChannel: this.defaultTriageChannel,
        defaultInternalOnly: true,
        defaultExternalOnly: false,
        hideChannelTypeFilter: true,
        hideSecondaryTeams: false,
      },
    });

    modal.present();

    modal.onDidDismiss().then(({ data }: any) => {
      if (data?.channel) {
        this.defaultTriageChannel = data.channel;
        this.defaultTriageChannelId = data.channel.id;
      }
    });
  }

  setDefaultTicketType() {
    this.searchablePopoverService.ticketTypes({
      event,
      showNoneOption: false,
      projectId: this.id,
      callback: (id: string, _title: string, data: TicketType) => {
        if (this.defaultTicketType?.id === id) {
          this.defaultTicketType = null;
        } else {
          this.defaultTicketType = data ?? undefined;
        }
      },
    });
  }

  async submit() {
    if (!this.name) {
      await this.msgSrvc.show('Whoops! Please enter a name.');

      return;
    }

    this.isLoading = true;

    const updates = {
      name: this.name,
      description: this.description,
      visibility: this.visibility,
      isExternal: this.isExternal,
      defaultTriageChannelId: this.defaultTriageChannelId,
      supportLogo: this.supportLogo,
      supportName: this.supportName,
      workingHours: this.workingHours,
      supportSteps: this.supportSteps,
      defaultTicketTypeId: this.defaultTicketType?.id,
      instructions: this.instructions,
      triageMessageTemplateId: this.triageMessageTemplateId,
      escalationSettings: this.escalationSettings,
    };

    try {
      if (this.id) {
        await this.projectsService.updatePartial(this.id, updates);

        if (this.originalName !== this.name) {
          window.location.reload();
        } else {
          await this.msgSrvc.show('Project updated!');
        }
      } else {
        await this.projectsService.create(updates);

        await this.msgSrvc.show('Project added!');
      }

      this.dismiss(this.id ? 'update' : 'add');
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    if (this.id === this.authService.tenant.defaultProjectId) {
      this.msgSrvc.show(
        `You can't delete the primary project. You can change the default project in your general settings`,
      );
      return;
    }
    const alert = await this.alertCtrl.create({
      header: 'Delete project?',
      subHeader: `This can't be undone. All customers in this project will be moved to the default project.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.projectsService.delete(this.id);
              this.isLoading = false;
              this.dismiss('delete');
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.show(`Unable to delete project: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }

  dismiss(action?: 'delete' | 'update' | 'add') {
    this.modalCtrl.dismiss({ action });
  }

  async uploadSupportLogo() {
    const modal = await this.modalCtrl.create({
      component: ImageModalComponent,
      componentProps: {
        searchValue: '',
        helperText: '',
        aspectRatio: 1,
      },
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();

    // if backdrop is clicked and no data passed
    if (!data || !data.uploadFile) {
      return;
    }

    this.uploadService
      .upload([data.uploadFile], `slack`, fileNameFromImageModalUpload(data.uploadFile))
      .then(() => {
        this.supportLogo = data.uploadFile.url;
      })
      .catch((err) => {
        this.msgSrvc.show(err.message);
      });
  }

  async manageWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.workingHours ?? {},
        onSave: async (updates: any) => {
          const updatedProject = await this.projectsService.updatePartial(this.id, updates);
          this.workingHours = updatedProject.workingHours;
          this.workingHoursEnabled = updatedProject.workingHours?.enabled ?? false;
        },
      },
    });

    modal.present();
  }

  supportStepsDidChange(supportSteps: any[]) {
    this.supportSteps = supportSteps;
  }

  viewInInbox() {
    this.dismiss();
    const filter = [
      [
        { label: 'Project', id: 'projectId', icon: 'folder-open-outline', type: 'select' },
        [{ id: this.id, label: this.name }],
      ],
    ];
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`, {
      queryParams: {
        filters: JSON.stringify(filter),
      },
      queryParamsHandling: 'merge',
    });
  }
}
